import { apiClient } from "@/lib/api-client";
import {
  Candidate,
  CandidateDocument,
  CandidateProfile,
  CandidateTimeline,
  DocumentDownloadResponse,
} from "../dtos/Candidate"
import type { components } from "@/generated/api-types"

// Enum types from generated API spec
export type VacancyStatus = components["schemas"]["ProTalent.Domain.Enums.VacancyStatus"]
export type ApplicationStage = components["schemas"]["ProTalent.Domain.Enums.ApplicationStage"]
export type CandidateSortField = components["schemas"]["ProTalent.Application.Enums.CandidateSortField"]
export type CandidateSkillsMatchMode = components["schemas"]["ProTalent.Application.Enums.CandidateSkillsMatchMode"]
export type SortDirection = components["schemas"]["ProTalent.Application.Enums.SortDirection"]

export type CandidateApplicationSummaryDto = components["schemas"]["ProTalent.Application.DTOs.CandidateApplicationSummaryDto"]
// TODO: drop the cvFileName extension once api-types is regenerated from a backend that returns it
export type CandidateSummaryDto = components["schemas"]["ProTalent.Application.DTOs.CandidateSummaryDto"]
export type PagedCandidatesResponse = components["schemas"]["ProTalent.Application.DTOs.PagedCandidatesResponse"]
export type DistinctSkillsResponse = components["schemas"]["ProTalent.Application.DTOs.DistinctSkillsResponse"]

export interface GetCandidatesPagedParams {
  search?: string
  vacancyIds?: string[]
  vacancyStatus?: VacancyStatus
  applicationStage?: ApplicationStage
  skills?: string[]
  skillsMatchMode?: CandidateSkillsMatchMode
  minExperienceYears?: number
  maxExperienceYears?: number
  location?: string
  hasCv?: boolean
  sortBy?: CandidateSortField
  sortDirection?: SortDirection
  page?: number
  pageSize?: number
}

export async function getCandidateDetails(
  organizationId: string | null,
  candidateId: string,
): Promise<Candidate> {
  const response = await apiClient.get<Candidate>(
    `/organizations/${organizationId}/candidates/${candidateId}`,
  )
  return response.data
}

export async function getCandidateTimeline(
  organizationId: string | null,
  candidateId: string,
): Promise<CandidateTimeline[]> {
  const response = await apiClient.get<CandidateTimeline[]>(
    `/organizations/${organizationId}/candidates/${candidateId}/timeline`,
  )
  return response.data
}

export async function getCandidateDocuments(
  organizationId: string | null,
  candidateId: string,
): Promise<CandidateDocument[]> {
  const response = await apiClient.get<CandidateDocument[]>(
    `/organizations/${organizationId}/candidates/${candidateId}/documents`,
  )
  return response.data
}

export async function getCandidateProfile(
  organizationId: string | null,
  candidateId: string,
): Promise<CandidateProfile> {
  const response = await apiClient.get<CandidateProfile>(
    `/organizations/${organizationId}/candidates/${candidateId}/profile`,
  )
  return response.data
}

/** Requests a short-lived SAS URL for the candidate's CV; call at click time, never cache. */
export async function getCandidateCvDownloadUrl(
  organizationId: string | null,
  candidateId: string,
): Promise<DocumentDownloadResponse> {
  const response = await apiClient.get<DocumentDownloadResponse>(
    `/organizations/${organizationId}/candidates/${candidateId}/cv/download-url`,
  )
  return response.data
}

export async function downloadCandidateDocument(
  organizationId: string | null,
  documentId: string,
): Promise<DocumentDownloadResponse> {
  const response = await apiClient.get<DocumentDownloadResponse>(
    `/organizations/${organizationId}/documents/${documentId}/download`,
  )
  return response.data
}


export async function uploadCandidateDocument(
  organizationId: string | null,
  candidateId: string,
  file: File,
  fileName?: string,
): Promise<void> {
  const formData = new FormData()
  formData.append("file", file)
  if (fileName) {
    formData.append("fileName", fileName)
  }
  await apiClient.post(
    `/organizations/${organizationId}/candidates/${candidateId}/documents`,
    formData,
  )
}

export async function getCandidatesPaged(
  organizationId: string,
  params: GetCandidatesPagedParams,
): Promise<PagedCandidatesResponse> {
  const response = await apiClient.get<PagedCandidatesResponse>(
    `/organizations/${organizationId}/candidates`,
    { params },
  )
  return response.data
}

export async function getDistinctSkills(
  organizationId: string,
): Promise<DistinctSkillsResponse> {
  const response = await apiClient.get<DistinctSkillsResponse>(
    `/organizations/${organizationId}/candidates/distinct-skills`,
  )
  return response.data
}
